// Payslip export — renders the payslip into a hidden iframe and opens the
// browser print dialog (whose destination includes "Save as PDF").
//
// This deliberately avoids window.open(): popup blockers — including the
// in-app browser's — silently swallow window.open('', '_blank'), which is why
// payslips previously "failed to generate" for many users. An iframe needs no
// permission and works identically.
//
// The line items are derived so they always reconcile to the net amount:
// earnings rows come from the stipend breakdown, and any gap between the
// breakdown total and the amount actually being paid (monthly cap, manual
// final adjustment) is shown as an explicit adjustment row. A payslip whose
// rows don't add up to the net is the classic "generated improperly" bug.
//
// Styling is self-contained CSS — no CDN and no web fonts — so the document
// renders instantly and identically offline, and prints cleanly to PDF.

import type { MonthlyReport } from '@/types/monthly'
import type { StipendExportEmployee } from '@/lib/export-stipend-report'

const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>'"]/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
}[char] || char))

const rupee = (value: number | null | undefined) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`

function monthLabel(month: string): string {
  const [year, monthNumber] = String(month || '').split('-')
  const date = new Date(Number(year), Number(monthNumber) - 1, 1)
  return Number.isNaN(date.getTime())
    ? String(month || '-')
    : date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
}

interface LineItem {
  label: string
  amount: number
  /** Negative amounts render as deductions. */
  kind?: 'earning' | 'adjustment'
  note?: string
}

export function exportPayslip(report: MonthlyReport, employee: StipendExportEmployee = {}) {
  const breakdown = report.stipendBreakdown
  const finalized = report.finalStipend != null
  const net = report.finalStipend ?? report.suggestedStipend ?? 0

  // Earnings as recorded by the generation run.
  const items: LineItem[] = []
  const hasBreakdown = !!breakdown
  if (hasBreakdown) {
    if (breakdown!.hourlyPay > 0) {
      items.push({
        label: 'Hourly pay',
        amount: breakdown!.hourlyPay,
        note: `${breakdown!.payableHours}h payable × hourly rate`,
      })
    }
    if (breakdown!.taskPay > 0) {
      items.push({
        label: 'Task bonus',
        amount: breakdown!.taskPay,
        note: `${report.tasks?.completed ?? 0} task(s) completed`,
      })
    }
    if (breakdown!.performanceBonus > 0) {
      items.push({
        label: 'Performance bonus',
        amount: breakdown!.performanceBonus,
        note: `${report.tier} tier × ${breakdown!.performanceMultiplier} multiplier`,
      })
    }
  } else {
    items.push({ label: 'Stipend (suggested)', amount: report.suggestedStipend ?? 0, note: 'Re-generate for a detailed breakdown' })
  }

  // Reconciliation: everything between the earnings subtotal and the net is an
  // explicit adjustment row, so the ledger can never silently disagree.
  if (breakdown?.cappedAt != null && breakdown.uncappedStipend > breakdown.cappedAt) {
    items.push({
      label: 'Monthly cap applied',
      amount: breakdown.cappedAt - breakdown.uncappedStipend,
      kind: 'adjustment',
      note: `Capped at ${rupee(breakdown.cappedAt)} (uncapped ${rupee(breakdown.uncappedStipend)})`,
    })
  }
  if (finalized && Math.abs(net - (report.suggestedStipend ?? 0)) > 0 && breakdown?.cappedAt == null) {
    items.push({
      label: 'Final adjustment (HR)',
      amount: net - (report.suggestedStipend ?? 0),
      kind: 'adjustment',
      note: 'Difference between suggested and finalized amount',
    })
  }
  // Safety net: if anything still doesn't reconcile (e.g. legacy reports saved
  // before the breakdown existed), show the remainder rather than print a
  // payslip whose rows don't add up.
  const shown = items.reduce((sum, item) => sum + item.amount, 0)
  if (Math.round(net - shown) !== 0) {
    items.push({ label: 'Other adjustments', amount: net - shown, kind: 'adjustment' })
  }

  const name = employee.name || report.userName || '-'
  const logoUrl = `${window.location.origin}/sasha-logo-small.png`
  const ref = `PAY-${String(report.userId || 'UNKNOWN').slice(0, 6).toUpperCase()}-${String(report.month || '').replace('-', '')}`
  const generatedOn = report.finalStipendAt || report.generatedAt

  const itemRows = items.map(item => {
    const negative = item.amount < 0
    return `
      <tr class="item ${item.kind === 'adjustment' ? 'adj' : ''}">
        <td>
          <div class="item-label">${escapeHtml(item.label)}</div>
          ${item.note ? `<div class="item-note">${escapeHtml(item.note)}</div>` : ''}
        </td>
        <td class="amount ${negative ? 'neg' : ''}">${negative ? '−' : ''}${escapeHtml(rupee(Math.abs(item.amount)))}</td>
      </tr>`
  }).join('')

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>Payslip — ${escapeHtml(name)} — ${escapeHtml(monthLabel(report.month))}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; background: #fafaf9; color: #171717; padding: 24px; display: flex; flex-direction: column; align-items: center; }
  .payslip { width: 100%; max-width: 720px; background: #fff; border-radius: 20px; border: 1px solid #e7e5e4; overflow: hidden; }
  .accent { height: 6px; background: linear-gradient(to right, #f97316, #ea580c); }
  .inner { padding: 28px 32px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 20px; border-bottom: 1px solid #f5f5f4; }
  .brand { display: flex; align-items: center; gap: 12px; }
  .brand img { height: 40px; width: auto; }
  .brand h1 { font-size: 17px; font-weight: 700; }
  .brand p { font-size: 11px; color: #a3a3a3; margin-top: 2px; }
  .badge { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; }
  .badge.paid { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
  .badge.review { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
  .slip-meta { text-align: right; }
  .slip-meta strong { font-size: 14px; display: block; margin-top: 6px; }
  .slip-meta span { font-size: 11px; color: #a3a3a3; }
  .cols { display: flex; gap: 24px; padding: 18px 0; border-bottom: 1px solid #f5f5f4; }
  .cols > div { flex: 1; }
  .cols h3, .section h3 { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #a8a29e; margin-bottom: 8px; font-weight: 700; }
  .kv { width: 100%; border-collapse: collapse; font-size: 13px; }
  .kv td { padding: 3px 0; vertical-align: top; }
  .kv td:first-child { color: #78716c; width: 38%; }
  .kv td:last-child { font-weight: 600; word-break: break-word; }
  .metrics { display: flex; gap: 12px; padding: 18px 0; border-bottom: 1px solid #f5f5f4; }
  .metric { flex: 1; text-align: center; background: #fafaf9; border: 1px solid #eee; border-radius: 14px; padding: 10px 6px; }
  .metric p { font-size: 9px; text-transform: uppercase; letter-spacing: 0.07em; color: #a8a29e; }
  .metric strong { display: block; font-size: 16px; margin-top: 2px; }
  table.items { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 4px; }
  table.items tr.item td { padding: 8px 0; border-bottom: 1px solid #f5f5f4; }
  .item-label { font-weight: 500; }
  .item-note { font-size: 11px; color: #a8a29e; margin-top: 1px; }
  tr.item.adj .item-label { color: #b45309; }
  td.amount { text-align: right; font-weight: 600; white-space: nowrap; }
  td.amount.neg { color: #dc2626; }
  tr.net td { padding: 14px 0 4px; border-bottom: 0; font-size: 15px; }
  tr.net td:first-child { font-weight: 700; }
  tr.net td.amount { font-size: 21px; font-weight: 800; color: #ea580c; }
  .safeguards { margin-top: 14px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 12px; padding: 10px 14px; font-size: 11px; color: #166534; }
  .safeguards p { font-weight: 700; margin-bottom: 4px; }
  .footnote { text-align: center; font-size: 10px; color: #d6d3d1; padding: 16px 0 6px; border-top: 1px solid #f5f5f4; margin-top: 18px; }
  @media print {
    body { background: #fff; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .payslip { border: 0; border-radius: 0; max-width: none; }
  }
</style>
</head>
<body>

  <div class="payslip">
    <div class="accent"></div>
    <div class="inner">
      <div class="header">
        <div class="brand">
          <img src="${logoUrl}" alt="Sasha Infinity" onerror="this.style.display='none'" />
          <div>
            <h1>Sasha Infinity</h1>
            <p>Attendance &amp; Stipend Management</p>
          </div>
        </div>
        <div class="slip-meta">
          <span class="badge ${finalized ? 'paid' : 'review'}">${finalized ? 'PAID' : 'REVIEW'}</span>
          <strong>Payslip for ${escapeHtml(monthLabel(report.month))}</strong>
          <span>Reference: ${escapeHtml(ref)}</span>
        </div>
      </div>

      <div class="cols">
        <div>
          <h3>Employee</h3>
          <table class="kv">
            <tr><td>Name</td><td>${escapeHtml(name)}</td></tr>
            <tr><td>Employee ID</td><td>${escapeHtml(employee.employeeId || '-')}</td></tr>
            <tr><td>Email</td><td>${escapeHtml(employee.email || '-')}</td></tr>
          </table>
        </div>
        <div>
          <h3>Organization</h3>
          <table class="kv">
            <tr><td>Department</td><td>${escapeHtml(employee.department || '-')}</td></tr>
            <tr><td>Position</td><td>${escapeHtml(employee.position || '-')}</td></tr>
            <tr><td>Issued</td><td>${escapeHtml(generatedOn ? new Date(generatedOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-')}</td></tr>
          </table>
        </div>
      </div>

      <div class="metrics">
        <div class="metric"><p>Payable hours</p><strong>${report.hours?.payable ?? report.hours?.total ?? 0}h</strong></div>
        <div class="metric"><p>Expected hours</p><strong>${report.hours?.expected ?? 0}h</strong></div>
        <div class="metric"><p>Present days</p><strong>${report.attendance?.present ?? 0}</strong></div>
        <div class="metric"><p>Leave days</p><strong>${report.attendance?.leave ?? 0}</strong></div>
        <div class="metric"><p>Tasks done</p><strong>${report.tasks?.completed ?? 0}</strong></div>
      </div>

      <div class="section">
        <h3>Earnings &amp; adjustments</h3>
        <table class="items">
          ${itemRows}
          <tr class="net">
            <td>Net stipend ${finalized ? '' : '(proposed)'}</td>
            <td class="amount">${escapeHtml(rupee(net))}</td>
          </tr>
        </table>
      </div>

      ${breakdown?.safeguards?.length ? `
      <div class="safeguards">
        <p>Notes</p>
        ${breakdown.safeguards.map(s => `<div>• ${escapeHtml(s)}</div>`).join('')}
      </div>` : ''}

      <div class="footnote">
        <p>This is a computer-generated document and does not require a physical signature.</p>
        <p>© ${new Date().getFullYear()} Sasha Infinity. All rights reserved.</p>
      </div>
    </div>
  </div>
</body>
</html>`

  // Deliver via a hidden iframe: no popup permission needed, and the print
  // dialog (with "Save as PDF") opens straight from the app tab. The iframe is
  // removed after the dialog closes or after a timeout as a safety net.
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.setAttribute('aria-hidden', 'true')
  iframe.setAttribute('title', 'payslip-print')

  const cleanup = () => {
    window.setTimeout(() => iframe.remove(), 60_000)
    window.removeEventListener('focus', cleanup)
  }
  window.addEventListener('focus', cleanup)

  document.body.appendChild(iframe)
  const doc = iframe.contentDocument
  if (!doc) {
    iframe.remove()
    throw new Error('Could not create the payslip document.')
  }
  doc.open()
  doc.write(html)
  doc.close()

  // Give the iframe (and its logo image) a beat to lay out before printing.
  window.setTimeout(() => {
    try {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    } catch {
      iframe.remove()
    }
  }, 350)
}
